import { useFormContext } from "react-hook-form";
import Select from "@medoma/components/Select/Select";
import { sitesQueryOptions } from "@/api/Patients";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { useQuery } from "@tanstack/react-query";
import { useLingui } from "@lingui/react/macro";
import type { SiteId } from "@medoma/models/sites";

export type SiteSelectFields = {
  // Empty string is the "nothing picked yet" sentinel for the multi-site case;
  // the required validator below blocks it from reaching submit.
  siteId: SiteId | "";
};

const SiteSelect = () => {
  const { t } = useLingui();
  const {
    register,
    formState: { errors },
  } = useFormContext<SiteSelectFields>();

  const {
    data: sites,
    isPending: isPendingSites,
    isError: isErrorSites,
  } = useQuery(sitesQueryOptions);

  if (isErrorSites) {
    return (
      <ErrorMessage
        message={t`Kunde inte hämta enhet. Försök igen om en liten stund. Kontakta support om problemet kvarstår.`}
      />
    );
  }

  if (isPendingSites) {
    return (
      <Select label={t`Enhet`} name="siteId">
        <option>{t`Hämtar enheter`}...</option>
      </Select>
    );
  }

  if (sites.length === 0) {
    return (
      <ErrorMessage
        message={t`Ingen enhet hittades. Försök igen om en liten stund. Kontakta support om problemet kvarstår.`}
      />
    );
  }

  return (
    <Select
      label={t`Enhet`}
      {...register("siteId", {
        required: { value: true, message: t`Ange enhet` },
      })}
      errorMessage={errors.siteId?.message}
    >
      <option value="" disabled>
        {t`Välj enhet`}
      </option>
      {sites.map((site) => (
        <option value={site.id} key={site.id}>
          {site.displayName}
        </option>
      ))}
    </Select>
  );
};

export default SiteSelect;
